import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { contentService, ExerciseFlowItemDto, PublicQuickLaunchCardDto } from '../services/contentService';
import { subscriptionService } from '../services/subscriptionService';
import { SubscriptionPlan } from '../types/subscription';
import { useNotifications } from './feedback/NotificationSystem';
import { PersianOverlayLoader } from './shared/PersianLoader';
import { ResponsiveContainer } from './shared/MobileResponsive';
import { CatalogFlowFilterCollapsible } from './shared/CatalogFlowFilterCollapsible';
import { CatalogFlowExercisePassedIcon, CatalogFlowTrophyBadge } from './shared/CatalogFlowTrophyBadge';
import { CatalogFlowPageHeader } from './shared/CatalogFlowPageHeader';
import { CatalogFlowHeaderProgressBar } from './shared/CatalogFlowHeaderProgressBar';
import { SimplePageHeader } from './shared/SimplePageHeader';
import { useLearnerHomeDesign } from '../utils/learnerHomeDesign';
import {
  catalogFlowExerciseActionBtnIconClass,
  catalogFlowExerciseActionBtnRowClass,
  catalogFlowExerciseActionBtnSizeClass,
} from './shared/catalogFlowItemStyles';
import { LessonMapPanel } from './learning/LessonMapPanel';
import { SubscriptionPlanIcons } from './shared/SubscriptionPlanIcons';
import { ExerciseLockedModal, ExerciseLockedRequiredPlan } from './shared/ExerciseLockedModal';
import { ClickableTooltip } from './shared/ClickableTooltip';
import { PenLine, CheckCircle, Circle, ExternalLink } from 'lucide-react';
import { ExerciseModal, ExerciseType } from './learning/ExerciseModal';
import { NonFarsiContentHintModal } from './learning/NonFarsiContentHintModal';
import { saveExerciseFlowPosition, exerciseFlowItemElementId } from '../utils/lastLearningPosition';
import { buildCatalogFlowCourseOptions } from '../utils/catalogFlowCourse';
import { buildCatalogFlowMap } from '../utils/catalogFlowMapSections';
import type { LessonMapItem } from '../utils/lessonMapSections';
import { FlowContextBreadcrumb } from './shared/FlowContextBreadcrumb';
import { useAppLocale } from '../providers/LocaleProvider';
import { useLocalizedContent } from '../hooks/useLocalizedContent';
import { useNonFarsiContentGate } from '../hooks/useNonFarsiContentGate';
import { isDesignedForNonFarsiUsers } from '../utils/nonFarsiContentHint';
import { pickLocalizedTitle } from '../utils/localizedContent';
import { notifyGamificationAfterAction } from '../utils/gamificationNotif';
import {
  readPersistedCatalogFlowFilters,
  writePersistedCatalogFlowFilters
} from '../utils/learnerFilterPersistence';

function requiredPlansFromIds(planIds: number[], plans: SubscriptionPlan[]): ExerciseLockedRequiredPlan[] {
  return planIds
    .map((id) => plans.find((p) => p.id === id))
    .filter((p): p is SubscriptionPlan => !!p)
    .map((p) => ({ id: p.id, name: p.name, ...(p.description != null && p.description !== '' ? { description: p.description } : {}) }));
}

type CatalogFilterKey = 'all' | 'done' | 'remaining';

interface ExerciseFlowProps {
  onBack: () => void;
  onBackToDashboard?: () => void;
  onNavigateToLesson?: (lessonId: number) => void;
  /** From keep learning — flash and scroll to the last exercise row without opening the modal. */
  highlightItem?: { flowKind: string; exerciseId: number };
  onHighlightConsumed?: () => void;
}

function mapExerciseKind(kind: string): ExerciseType {
  const k = (kind || '').toLowerCase();
  if (k === 'course') return 'course';
  if (k === 'level') return 'level';
  return 'lesson';
}

function viewLessonId(item: ExerciseFlowItemDto): number | null {
  const v = item.viewLessonId ?? item.lessonId;
  if (v == null || Number(v) <= 0) return null;
  return Number(v);
}

const PAGE_SIZE = 10;
const EXERCISES_LAUNCH_DEFAULT_COLOR = '#d97706';
const HIGHLIGHT_DURATION_MS = 5000;

export const ExerciseFlow: React.FC<ExerciseFlowProps> = ({
  onBack,
  onBackToDashboard,
  onNavigateToLesson,
  highlightItem,
  onHighlightConsumed
}) => {
  const { t } = useTranslation(['learning', 'common']);
  const { direction, locale } = useAppLocale();
  const { pickTitle: pickUiTitle } = useLocalizedContent();
  const isRtl = locale === 'fa' || direction === 'rtl';
  const [homeDesign] = useLearnerHomeDesign();
  const simpleHome = homeDesign === 'mme';
  const { showError, showSuccess } = useNotifications();
  const {
    gateState: nonFarsiGateState,
    gateClick: gateNonFarsiClick,
    openHintManually: openNonFarsiHintManually,
    handleDismiss: handleNonFarsiGateDismiss,
    handleContinue: handleNonFarsiGateContinue,
    isRtl: nonFarsiGateRtl,
  } = useNonFarsiContentGate();
  const pickItemTitle = (item: ExerciseFlowItemDto) =>
    isDesignedForNonFarsiUsers(item)
      ? pickLocalizedTitle(item, 'en')
      : pickUiTitle(item);
  const [items, setItems] = useState<ExerciseFlowItemDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeItem, setActiveItem] = useState<ExerciseFlowItemDto | null>(null);
  const [markingId, setMarkingId] = useState<string | null>(null);
  const [filter, setFilter] = useState<CatalogFilterKey>(() => readPersistedCatalogFlowFilters('exercise').filter);
  const [courseFilter, setCourseFilter] = useState<number | null>(
    () => readPersistedCatalogFlowFilters('exercise').courseFilter
  );
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [subscriptionPlans, setSubscriptionPlans] = useState<SubscriptionPlan[]>([]);
  const [lockedExerciseModal, setLockedExerciseModal] = useState<{
    exerciseTitle: string;
    requiredPlans: ExerciseLockedRequiredPlan[];
  } | null>(null);
  const [passedLottieUnavailable, setPassedLottieUnavailable] = useState(false);
  const [launchCard, setLaunchCard] = useState<PublicQuickLaunchCardDto | null>(null);
  const [activeHighlightKey, setActiveHighlightKey] = useState<string | null>(null);
  const highlightTimerRef = useRef<number | null>(null);
  const highlightHandledRef = useRef<string | null>(null);

  useEffect(() => {
    fetch('/animations/Passed.lottie', { method: 'HEAD' })
      .then((r) => {
        if (!r.ok) setPassedLottieUnavailable(true);
      })
      .catch(() => setPassedLottieUnavailable(true));
  }, []);

  useEffect(() => {
    let cancelled = false;
    contentService
      .getPublicQuickLaunchCards()
      .then((cards) => {
        if (!cancelled) {
          setLaunchCard(cards.find((c) => c.cardKey === 'exercises') ?? null);
        }
      })
      .catch(() => {
        if (!cancelled) setLaunchCard(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const reload = useCallback(async () => {
    const list = await contentService.getExerciseFlowItems();
    setItems(Array.isArray(list) ? list : []);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        await reload();
      } catch (e) {
        if (!cancelled) {
          showError(e instanceof Error ? e.message : t('exerciseFlow.loadFailed'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // Initial load only — showError/t change when i18n reloads and must not re-fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reload]);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [filter, courseFilter]);

  useEffect(() => {
    writePersistedCatalogFlowFilters('exercise', { filter, courseFilter });
  }, [filter, courseFilter]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const plans = await subscriptionService.getPlans();
        if (!cancelled) setSubscriptionPlans(plans);
      } catch {
        if (!cancelled) setSubscriptionPlans([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const courseOptions = useMemo(() => buildCatalogFlowCourseOptions(items, locale), [items, locale]);

  const courseScopedItems = useMemo(() => {
    if (courseFilter == null) return items;
    return items.filter((i) => i.courseId === courseFilter);
  }, [items, courseFilter]);

  const filteredItems = useMemo(() => {
    if (filter === 'done') return courseScopedItems.filter((i) => i.isReviewedByUser);
    if (filter === 'remaining') return courseScopedItems.filter((i) => !i.isReviewedByUser);
    return courseScopedItems;
  }, [courseScopedItems, filter]);

  const reviewedCount = useMemo(
    () => courseScopedItems.filter((i) => i.isReviewedByUser).length,
    [courseScopedItems]
  );
  const remainingCount = useMemo(
    () => courseScopedItems.length - reviewedCount,
    [courseScopedItems, reviewedCount]
  );
  const totalReviewedCount = useMemo(() => items.filter((i) => i.isReviewedByUser).length, [items]);

  useEffect(() => {
    if (loading) return;
    const summary =
      items.length > 0
        ? t('exerciseFlow.positionSummary', { done: totalReviewedCount, total: items.length })
        : undefined;
    saveExerciseFlowPosition(t('quickActionExercises'), summary, undefined, {
      touchUpdatedAt: false,
      progress: { done: totalReviewedCount, total: items.length }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- update when counts change, not on every t identity change
  }, [loading, totalReviewedCount, items.length]);

  const positionSummary =
    items.length > 0
      ? t('exerciseFlow.positionSummary', { done: totalReviewedCount, total: items.length })
      : undefined;

  const rememberExerciseItem = useCallback(
    (item: ExerciseFlowItemDto) => {
      saveExerciseFlowPosition(t('quickActionExercises'), positionSummary, {
        flowKind: item.kind,
        exerciseId: item.exerciseId,
        itemTitle: item.title,
        itemTitleFa: item.titleFa
      }, { progress: { done: totalReviewedCount, total: items.length } });
    },
    [positionSummary, pickItemTitle, t]
  );

  useEffect(() => {
    if (!highlightItem || highlightItem.exerciseId <= 0 || loading) return;
    const target = items.find(
      (i) => i.exerciseId === highlightItem.exerciseId && i.kind === highlightItem.flowKind
    );
    if (!target) return;
    setFilter('all');
    setCourseFilter(target.courseId > 0 ? target.courseId : null);
  }, [highlightItem, loading, items]);

  useEffect(() => {
    if (!highlightItem || highlightItem.exerciseId <= 0) {
      highlightHandledRef.current = null;
      return;
    }
    if (loading) return;

    const requestKey = `${highlightItem.flowKind}-${highlightItem.exerciseId}`;
    if (highlightHandledRef.current === requestKey) return;

    const targetInAll = items.find(
      (i) => i.exerciseId === highlightItem.exerciseId && i.kind === highlightItem.flowKind
    );
    if (!targetInAll) {
      if (items.length > 0) {
        highlightHandledRef.current = requestKey;
        onHighlightConsumed?.();
      }
      return;
    }

    const targetIndex = filteredItems.findIndex(
      (i) => i.exerciseId === highlightItem.exerciseId && i.kind === highlightItem.flowKind
    );
    if (targetIndex < 0) return;

    if (targetIndex >= visibleCount) {
      setVisibleCount(targetIndex + 1);
      return;
    }

    highlightHandledRef.current = requestKey;
    const target = filteredItems[targetIndex];
    const highlightKey = `${target.kind}-${target.exerciseId}`;
    setActiveHighlightKey(highlightKey);

    const rafId = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        document
          .getElementById(exerciseFlowItemElementId(target.kind, target.exerciseId))
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    });

    if (highlightTimerRef.current != null) {
      window.clearTimeout(highlightTimerRef.current);
    }
    highlightTimerRef.current = window.setTimeout(() => {
      setActiveHighlightKey(null);
      onHighlightConsumed?.();
      highlightTimerRef.current = null;
    }, HIGHLIGHT_DURATION_MS);

    return () => {
      cancelAnimationFrame(rafId);
      if (highlightTimerRef.current != null) {
        window.clearTimeout(highlightTimerRef.current);
        highlightTimerRef.current = null;
      }
    };
  }, [highlightItem, loading, items, filteredItems, visibleCount, onHighlightConsumed]);

  const launchAccentColor = launchCard?.color || EXERCISES_LAUNCH_DEFAULT_COLOR;

  const pagedItems = useMemo(() => filteredItems.slice(0, visibleCount), [filteredItems, visibleCount]);

  const exerciseMapCurrentKey = activeItem
    ? `${activeItem.kind}-${activeItem.exerciseId}`
    : activeHighlightKey;

  const exerciseMapData = useMemo(
    () =>
      buildCatalogFlowMap(
        items.map((item) => ({
          kind: item.kind,
          entityId: item.exerciseId,
          courseId: item.courseId,
          levelId: item.levelId,
          lessonId: item.lessonId,
          title: item.title,
          titleFa: item.titleFa,
          contextSubtitle: item.contextSubtitle,
          contextSubtitleFa: item.contextSubtitleFa,
          isCompleted: item.isReviewedByUser,
          isLocked: !item.isAccessibleToUser,
          subscriptionPlanIds: item.subscriptionPlanIds
        })),
        {
          locale,
          currentKey: exerciseMapCurrentKey,
          courseFilter,
          mapTitle: t('exerciseMap.title')
        }
      ),
    [items, locale, exerciseMapCurrentKey, courseFilter, t]
  );

  const markReviewed = async (item: ExerciseFlowItemDto) => {
    const key = `${item.kind}-${item.exerciseId}`;
    try {
      setMarkingId(key);
      await contentService.markExerciseReviewed(item.kind, item.exerciseId);
      setItems((prev) =>
        prev.map((i) =>
          i.exerciseId === item.exerciseId && i.kind === item.kind
            ? { ...i, isReviewedByUser: true }
            : i
        )
      );
      showSuccess(t('exerciseFlow.markedReviewedToast'));
      notifyGamificationAfterAction();
      await reload();
    } catch (e) {
      showError(e instanceof Error ? e.message : t('exerciseFlow.couldNotSaveReview'));
    } finally {
      setMarkingId(null);
    }
  };

  const openPractice = async (item: ExerciseFlowItemDto) => {
    const planIds = item.subscriptionPlanIds ?? [];
    const requiredPlans = requiredPlansFromIds(planIds, subscriptionPlans);

    if (!item.isAccessibleToUser) {
      setLockedExerciseModal({ exerciseTitle: pickItemTitle(item), requiredPlans });
      return;
    }
    try {
      const k = (item.kind || '').toLowerCase();
      if (k === 'course') await contentService.getCourseExerciseForLearner(item.exerciseId);
      else if (k === 'level') await contentService.getLevelExerciseForLearner(item.exerciseId);
      else await contentService.getLessonExerciseForLearner(item.exerciseId);
      rememberExerciseItem(item);
      setActiveItem(item);
    } catch (err) {
      const statusCode =
        typeof err === 'object' && err !== null && 'statusCode' in err ? (err as { statusCode: number }).statusCode : undefined;
      const msg = err instanceof Error ? err.message : '';
      const isNotAccessible =
        statusCode === 401 ||
        statusCode === 403 ||
        statusCode === 404 ||
        msg.toLowerCase().includes('not accessible') ||
        msg.toLowerCase().includes('not found');
      if (isNotAccessible) {
        setLockedExerciseModal({ exerciseTitle: pickItemTitle(item), requiredPlans });
      } else {
        showError(msg || t('exerciseFlow.loadExerciseFailed'));
      }
    }
  };

  const handleExerciseMapNavigate = useCallback(
    (mapItem: LessonMapItem) => {
      const kind = mapItem.flowKind ?? 'lesson';
      const target = items.find((i) => i.exerciseId === mapItem.entityId && i.kind === kind);
      if (!target) return;

      setCourseFilter(target.courseId > 0 ? target.courseId : null);
      setFilter('all');

      const inCourse = items.filter((i) => i.courseId === target.courseId);
      const idx = inCourse.findIndex((i) => i.exerciseId === target.exerciseId && i.kind === target.kind);
      if (idx >= 0) setVisibleCount(Math.max(PAGE_SIZE, idx + 1));

      const highlightKey = `${target.kind}-${target.exerciseId}`;
      setActiveHighlightKey(highlightKey);
      if (highlightTimerRef.current != null) window.clearTimeout(highlightTimerRef.current);
      highlightTimerRef.current = window.setTimeout(() => {
        setActiveHighlightKey(null);
        highlightTimerRef.current = null;
      }, HIGHLIGHT_DURATION_MS);

      // Focus the grid row only — do not open the exercise.
      window.setTimeout(() => {
        document
          .getElementById(exerciseFlowItemElementId(target.kind, target.exerciseId))
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 50);
    },
    [items]
  );

  const unmarkReviewed = async (item: ExerciseFlowItemDto) => {
    const key = `${item.kind}-${item.exerciseId}`;
    try {
      setMarkingId(key);
      await contentService.markExerciseUnreviewed(item.kind, item.exerciseId);
      setItems((prev) =>
        prev.map((i) =>
          i.exerciseId === item.exerciseId && i.kind === item.kind
            ? { ...i, isReviewedByUser: false }
            : i
        )
      );
      showSuccess(t('exerciseFlow.unmarkedToast'));
      await reload();
    } catch (e) {
      showError(e instanceof Error ? e.message : t('exerciseFlow.couldNotRemoveReview'));
    } finally {
      setMarkingId(null);
    }
  };

  if (loading) {
    return <PersianOverlayLoader isVisible message={t('exercise.loading')} />;
  }

  if (items.length === 0) {
    return (
      <ResponsiveContainer maxWidth="xl">
        <div className="learner-flow-page mme-tone py-12 text-center">
          <PenLine className="mx-auto mb-4 h-16 w-16 text-gray-400" />
          <p className="text-gray-600 dark:text-gray-400">{t('exerciseFlow.noExercisesYet')}</p>
          <button type="button" onClick={onBack} className="mt-4 rounded-lg bg-amber-600 px-4 py-2 text-white hover:bg-amber-500 dark:bg-amber-600 dark:hover:bg-amber-500">
            {t('common:back')}
          </button>
        </div>
      </ResponsiveContainer>
    );
  }

  return (
    <div className="learner-flow-page mme-tone min-h-screen bg-stone-50 pb-10 dark:bg-gray-950">
      <ResponsiveContainer maxWidth="xl" className="space-y-4 py-4 sm:py-6">
        {simpleHome ? (
          <>
            <SimplePageHeader tourId="exercise-flow-header" title={t('quickActionExercises')} onBack={onBack} />
            <CatalogFlowHeaderProgressBar done={totalReviewedCount} total={items.length} accent="amber" variant="exercise" />
          </>
        ) : (
        <CatalogFlowPageHeader
          title={t('quickActionExercises')}
          subtitle={t('exerciseFlow.subtitle')}
          icon={PenLine}
          accentColor={launchAccentColor}
          guideAccent="amber"
          onBack={onBack}
          onBackToDashboard={onBackToDashboard ?? onBack}
          statsDone={totalReviewedCount}
          statsTotal={items.length}
          progressVariant="exercise"
          tourId="exercise-flow-header"
        />
        )}

        <div data-tour-id="exercise-flow-filters">
        <CatalogFlowFilterCollapsible
          mode="exercise"
          filter={filter}
          onFilterChange={setFilter}
          countAll={courseScopedItems.length}
          countDone={reviewedCount}
          countRemaining={remainingCount}
          show={items.length > 0}
          collapsible={false}
          courseOptions={courseOptions}
          courseFilter={courseFilter}
          onCourseFilterChange={setCourseFilter}
        />
        </div>

        <div className="space-y-3">
          {filteredItems.length === 0 ? (
            <div className="rounded-2xl border border-amber-200/90 bg-white/90 p-6 text-center text-sm text-gray-600 shadow-sm dark:border-amber-900/35 dark:bg-gray-900/90 dark:text-gray-400 dark:shadow-lg dark:shadow-black/35">
              {t('exerciseFlow.noExercisesFilter')}
            </div>
          ) : (
            pagedItems.map((item, index) => {
              const markKey = `${item.kind}-${item.exerciseId}`;
              const isHighlighted = activeHighlightKey === markKey;
              const lessonNav = viewLessonId(item);
              return (
                <div
                  key={markKey}
                  id={exerciseFlowItemElementId(item.kind, item.exerciseId)}
                  className={`grid gap-x-3 gap-y-1 rounded-xl border p-4 shadow-sm transition-shadow dark:shadow-lg dark:shadow-black/35 ${
                    item.isReviewedByUser
                      ? 'grid-cols-[auto_minmax(0,1fr)_auto] sm:grid-cols-[auto_minmax(0,1fr)_auto_auto] sm:items-center'
                      : 'grid-cols-[auto_minmax(0,1fr)] sm:grid-cols-[auto_minmax(0,1fr)_auto] sm:items-center'
                  } ${isHighlighted ? 'search-result-highlight scroll-mt-24' : ''} ${
                    item.isReviewedByUser
                      ? 'border-green-200/80 bg-green-50 hover:bg-green-100 dark:border-emerald-500/45 dark:bg-emerald-900/55 dark:hover:bg-emerald-800/50'
                      : 'border-amber-200/75 bg-white/95 hover:border-amber-300/90 dark:border-gray-600 dark:bg-gray-900/95 dark:hover:border-gray-500'
                  }`}
                >
                  <div className="flex shrink-0 items-center gap-1.5 self-start sm:self-center">
                    <ClickableTooltip tooltip={t('exerciseFlow.exerciseTooltip')}>
                      <div className="rounded-xl bg-amber-100/95 p-2 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200">
                        <PenLine className="h-5 w-5" />
                      </div>
                    </ClickableTooltip>
                    <SubscriptionPlanIcons planIds={item.subscriptionPlanIds ?? []} plans={subscriptionPlans} compact />
                  </div>
                  <p className="min-w-0 self-center font-medium leading-snug text-gray-900 dark:text-gray-100">
                    {pickItemTitle(item)}
                  </p>
                  {item.isReviewedByUser ? (
                    <CatalogFlowExercisePassedIcon
                      lottieUnavailable={passedLottieUnavailable}
                      className="col-start-3 row-start-1 self-center sm:row-span-4"
                    />
                  ) : null}
                  <div
                    className={`min-w-0 text-start ${
                      item.isReviewedByUser
                        ? 'col-span-3 sm:col-span-1 sm:col-start-2'
                        : 'col-span-2 sm:col-span-1 sm:col-start-2'
                    }`}
                  >
                    <FlowContextBreadcrumb
                      contextSubtitle={item.contextSubtitle}
                      contextSubtitleFa={item.contextSubtitleFa}
                      isRtl={isRtl}
                    />
                    {item.isReviewedByUser ? (
                      <CatalogFlowTrophyBadge variant="exercise-reviewed" lottieUnavailable={passedLottieUnavailable} />
                    ) : null}
                  </div>
                  <div
                    className={`${catalogFlowExerciseActionBtnRowClass} ${
                      item.isReviewedByUser
                        ? 'col-span-3 sm:col-span-1 sm:col-start-4 sm:row-start-1 sm:row-span-4 sm:self-center'
                        : 'col-span-2 sm:col-span-1 sm:col-start-3 sm:row-start-1 sm:row-span-2 sm:self-center'
                    }`}
                  >
                    {onNavigateToLesson && lessonNav != null && (
                      <button
                        type="button"
                        {...(index === 0 ? { 'data-tour-id': 'exercise-flow-view-lesson' } : {})}
                        onClick={() => onNavigateToLesson(lessonNav)}
                        className={`flex items-center gap-1 rounded-xl bg-blue-600 font-medium text-white shadow-sm transition-colors hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 ${catalogFlowExerciseActionBtnSizeClass}`}
                      >
                        <ExternalLink className={catalogFlowExerciseActionBtnIconClass} />
                        {t('exerciseFlow.viewFullLesson')}
                      </button>
                    )}
                    {item.isReviewedByUser ? (
                      <button
                        type="button"
                        {...(index === 0 ? { 'data-tour-id': 'exercise-flow-unmark' } : {})}
                        disabled={markingId === markKey || !item.isAccessibleToUser}
                        title={!item.isAccessibleToUser ? t('exerciseFlow.subscriptionRequiredTooltip') : undefined}
                        onClick={() => unmarkReviewed(item)}
                        className={`flex items-center gap-1 rounded-xl border-2 border-amber-300/90 bg-amber-50 font-medium text-amber-950 shadow-sm transition-all hover:bg-amber-100 disabled:opacity-50 dark:border-amber-600/50 dark:bg-amber-950/30 dark:text-amber-100 dark:hover:bg-amber-900/35 ${catalogFlowExerciseActionBtnSizeClass}`}
                      >
                        <Circle className={catalogFlowExerciseActionBtnIconClass} />
                        {markingId === markKey ? t('exerciseFlow.saving') : t('exerciseFlow.unmark')}
                      </button>
                    ) : (
                      <button
                        type="button"
                        {...(index === 0 ? { 'data-tour-id': 'exercise-flow-mark' } : {})}
                        disabled={markingId === markKey || !item.isAccessibleToUser}
                        title={!item.isAccessibleToUser ? t('exerciseFlow.subscriptionRequiredTooltip') : undefined}
                        onClick={() => markReviewed(item)}
                        className={`flex items-center gap-1 rounded-xl bg-green-600 font-medium text-white shadow-sm transition-colors hover:bg-green-700 disabled:opacity-50 dark:bg-green-500 dark:hover:bg-green-600 ${catalogFlowExerciseActionBtnSizeClass}`}
                      >
                        <CheckCircle className={catalogFlowExerciseActionBtnIconClass} />
                        {markingId === markKey ? t('exerciseFlow.saving') : t('exerciseFlow.markCompletelyLearned')}
                      </button>
                    )}
                    <button
                      type="button"
                      {...(index === 0 ? { 'data-tour-id': 'exercise-flow-practice' } : {})}
                      onClick={(e) =>
                        gateNonFarsiClick(e, item, 'exercise', item.exerciseId, () => {
                          void openPractice(item);
                        })
                      }
                      className={`rounded-xl bg-gradient-to-r from-amber-600 to-amber-700 font-medium text-white hover:from-amber-500 hover:to-amber-600 dark:from-amber-600 dark:to-amber-800 ${catalogFlowExerciseActionBtnSizeClass}`}
                    >
                      {t('exerciseFlow.practice')}
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {filteredItems.length > visibleCount && (
          <div className="flex justify-center pt-1">
            <button
              type="button"
              onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
              className="rounded-xl border border-amber-200/90 bg-white px-5 py-2 text-sm font-medium text-amber-950 shadow-sm transition-colors hover:bg-amber-50 dark:border-amber-800/50 dark:bg-gray-900 dark:text-amber-100 dark:hover:bg-gray-800"
            >
              {t('exerciseFlow.moreLeft', { count: filteredItems.length - visibleCount })}
            </button>
          </div>
        )}
      </ResponsiveContainer>

      {activeItem && (
        <ExerciseModal
          key={`${activeItem.kind}-${activeItem.exerciseId}`}
          exerciseType={mapExerciseKind(activeItem.kind)}
          exerciseId={activeItem.exerciseId}
          contentForceEnglish={isDesignedForNonFarsiUsers(activeItem)}
          markedAsReviewedByUser={activeItem.isReviewedByUser}
          onClose={() => setActiveItem(null)}
          {...(locale !== 'en' && isDesignedForNonFarsiUsers(activeItem)
            ? { onEnglishLockedHintClick: () => openNonFarsiHintManually('exercise', activeItem.exerciseId) }
            : {})}
        />
      )}

      <NonFarsiContentHintModal
        open={nonFarsiGateState.open}
        isRtl={nonFarsiGateRtl}
        anchorRect={nonFarsiGateState.anchorRect}
        contentKey={nonFarsiGateState.contentKey}
        onDismiss={handleNonFarsiGateDismiss}
        onContinue={handleNonFarsiGateContinue}
      />

      <ExerciseLockedModal
        open={!!lockedExerciseModal}
        onClose={() => setLockedExerciseModal(null)}
        {...(lockedExerciseModal?.exerciseTitle ? { exerciseTitle: lockedExerciseModal.exerciseTitle } : {})}
        requiredPlans={lockedExerciseModal?.requiredPlans ?? []}
      />

      <LessonMapPanel
        mapData={exerciseMapData}
        onNavigate={handleExerciseMapNavigate}
        persistOpenKey="exercise-flow-map-open"
        subscriptionPlans={subscriptionPlans}
        animateFocusChange
        labels={{
          title: t('exerciseMap.title'),
          openAria: t('exerciseMap.openAria'),
          closeAria: t('exerciseMap.closeAria'),
          bubbleTitle: t('exerciseMap.bubbleTitle'),
          navAria: t('exerciseMap.navAria'),
          empty: t('exerciseMap.empty')
        }}
      />
    </div>
  );
};
